import { useContext, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import i18n from "i18n";

import { Feature, Floors } from "../../../../redux/services/config";
import { SearchResultStore } from "../../../../hooks/useSearchResults";
import { getFloorDifference } from "../../../../utils/getFloorDifference";
import { getFormattedFloors } from "../../../../utils/getFormattedFloors";
import { regexify } from "../../../../utils/regexifySearchTerm";
import { parseLanguageObject } from "utils/parseLanguageObject";

import { MapContext } from "../../Map";
import SearchResult from "../../../SearchResult/SearchResult";
import SearchResultGroup from "../../../SearchResult/components/SearchResultGroup/SearchResultGroup";

import styles from "./SearchResults.module.scss";

export interface SearchResultsProps {
  dataQA: string;
  results: SearchResultStore;
  searchTerm: string;
  className?: string;
  enableImages: boolean;
  onResultClick: (features: Feature[]) => void;
  onScroll?: () => void;
  floors: Floors;
  defaultFloorId: number;
  isTitleSearch?: boolean;
}

// Helper function to highlight search terms
const wrapSearchTerm = (searchTerm: string, text: string): string => {
  const regex = regexify(searchTerm);
  return text.replace(regex, "<span>$&</span>").replace(/ /g, "&nbsp;");
};

const SearchResults: React.FC<SearchResultsProps> = ({
  dataQA,
  results = {},
  searchTerm,
  onResultClick,
  onScroll,
  className,
  enableImages,
  floors,
  defaultFloorId,
  isTitleSearch = false,
}) => {
  const { t } = useTranslation();
  const { controlTheme } = useContext(MapContext);

  const formattedFloors = useMemo(() => getFormattedFloors(floors), [floors]);

  const hasResults = Object.keys(results).length > 0;

  return (
    <div
      data-qa={dataQA}
      className={classNames(styles.container, styles[controlTheme], className)}
      onClick={(e) => e.stopPropagation()}
    >
      {hasResults ? (
        <div className={styles.scrollContainer} onScroll={onScroll}>
          <ul className={styles.listContainer}>
            {Object.entries(results).map(([resultKey, features]) => {
              const firstFeature = features[0];
              const props = {
                title: isTitleSearch
                  ? wrapSearchTerm(
                      searchTerm,
                      parseLanguageObject(
                        firstFeature.properties.popup_header,
                        i18n.language,
                      ) || "",
                    )
                  : parseLanguageObject(
                      firstFeature.properties.popup_header,
                      i18n.language,
                    ) || "",
                className: styles.searchResult,
                showImage: enableImages,
                imageUrl: firstFeature.properties.popup_image_url,
                onClick: () => onResultClick(features),
                titleSearch: isTitleSearch,
              };

              // Render a group if there are multiple features
              if (features.length > 1) {
                return (
                  <SearchResultGroup
                    dataQA="search-results-group"
                    key={firstFeature.id}
                    {...props}
                  />
                );
              }

              // Render a single result
              const { amount, direction } = getFloorDifference(
                firstFeature.properties.floor_id,
                defaultFloorId,
                floors,
              );

              const category = firstFeature.properties.popup_subheader
                ? parseLanguageObject(
                    firstFeature.properties.popup_subheader,
                    i18n.language,
                  )
                : "";

              const floorNameLanguageObject = formattedFloors.find(
                ({ id }) => id === firstFeature.properties.floor_id,
              )?.name;

              const floorName = floorNameLanguageObject
                ? parseLanguageObject(floorNameLanguageObject, i18n.language)
                : "";

              return (
                <SearchResult
                  dataQA="search-result"
                  key={firstFeature.id}
                  category={category}
                  floorName={floorName || ""}
                  openingTimes={firstFeature.properties.opening_times}
                  isTemporarilyClosed={
                    firstFeature.properties.is_temporarily_closed
                  }
                  floorChange={{ direction, amount }}
                  {...props}
                />
              );
            })}
          </ul>
        </div>
      ) : (
        <div className={styles.noResultsContainer}>
          <div className={styles.title}>{t("search_results.no_results")}</div>
          <div className={styles.paragraph}>
            {t("search_results.no_results_message")}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchResults;
